"use client";

import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DirectionalIcon } from "@/components/ui/directional-icon";
import { getPrevNext, type DocsNavItem } from "@/lib/docs-nav";

type DocPageNavProps = {
  prev?: DocsNavItem;
  next?: DocsNavItem;
};

export function DocPageNav({ prev, next }: DocPageNavProps) {
  const t = useTranslations("docs.nav");
  const pathname = usePathname();
  const auto = getPrevNext(pathname);
  const prevLink = prev ?? auto.prev;
  const nextLink = next ?? auto.next;

  if (!prevLink && !nextLink) return null;

  return (
    <nav
      aria-label={t("pageNavAriaLabel")}
 className="flex items-center justify-between gap-4 border-t pt-8 text-sm"
    >
      <div className="flex-1">
        {prevLink && (
          <Link
            href={prevLink.href}
 className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <DirectionalIcon icon={ChevronLeft} className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">{t("previous")}: </span>
            {t(prevLink.labelKey)}
          </Link>
        )}
      </div>
      <div className="flex-1 text-end">
        {nextLink && (
          <Link
            href={nextLink.href}
 className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <span className="sr-only">{t("next")}: </span>
            {t(nextLink.labelKey)}
            <DirectionalIcon icon={ChevronRight} className="h-3.5 w-3.5" aria-hidden />
          </Link>
        )}
      </div>
    </nav>
  );
}
